<template>
  <v-container class="pt-7 pb-11">
    <v-row class="ma-0 mb-5" align="center">
      <h1 class="text-h4">{{ 'smartQuoteMonitor'|localize }}</h1>
      <v-spacer />
      <v-btn
        color="primary"
        depressed
        rounded
        :loading="processingMailbox"
        :disabled="processingMailbox"
        @click="processMailbox"
      >
        <v-icon size="20" left>mdi-email-sync</v-icon>
        {{ 'processEmail'|localize }}
      </v-btn>
    </v-row>

    <v-row class="mb-5">
      <v-col v-for="stat in statCards" :key="stat.key" cols="6" sm="6" md="4" lg>
        <v-tooltip top color="black" max-width="320">
          <template v-slot:activator="{ on, attrs }">
            <div
              class="smart-quote-stat-card"
              :class="`smart-quote-stat-card--${stat.variant}`"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon class="smart-quote-stat-card__hint-icon" size="16" color="grey">mdi-information-outline</v-icon>
              <div class="smart-quote-stat-card__icon" :class="`smart-quote-stat-card__icon--${stat.variant}`">
                <v-icon :color="stat.variant" size="22">{{ stat.icon }}</v-icon>
              </div>
              <div class="smart-quote-stat-card__text">
                <div class="smart-quote-stat-card__label">{{ stat.label }}</div>
                <div class="smart-quote-stat-card__value">{{ stat.value }}</div>
              </div>
            </div>
          </template>
          <span>{{ stat.hint }}</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <div class="white elevation-2 rounded">
      <v-row class="ma-0 pa-4 pb-0" dense>
        <v-col cols="12" sm="6" md="3">
          <div class="text-caption grey--text mb-1">{{ 'smartQuoteFilterDateRange'|localize }}</div>
          <v-menu
            ref="dateMenu"
            v-model="dateMenu"
            :close-on-content-click="false"
            :return-value.sync="dateRange"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="dateRangeText"
                readonly
                dense
                hide-details
                outlined
                clearable
                prepend-inner-icon="mdi-calendar"
                v-on="on"
                @click:clear="dateRange = []"
              />
            </template>
            <v-date-picker
              v-model="dateRange"
              no-title
              scrollable
              range
              @input="dateRange.length === 2 && $refs.dateMenu.save(dateRange)"
            />
          </v-menu>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="text-caption grey--text mb-1">{{ 'status'|localize }}</div>
          <v-select v-model="statusFilter" :items="statusOptions" dense hide-details outlined @change="onStatusSelectChange" />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="text-caption grey--text mb-1">{{ 'smartQuoteColumnCarrier'|localize }}</div>
          <v-text-field
            v-model.trim="carrierFilter"
            dense
            hide-details
            outlined
            clearable
            append-icon="mdi-magnify"
            :placeholder="'smartQuoteCarrierSearchPlaceholder'|localize"
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="text-caption grey--text mb-1">{{ 'smartQuoteRequestId'|localize }}</div>
          <v-text-field
            v-model.trim="requestIdSearch"
            dense
            hide-details
            outlined
            clearable
            append-icon="mdi-magnify"
            :placeholder="'smartQuoteSearchIdPlaceholder'|localize"
          />
        </v-col>
      </v-row>

      <div class="d-flex flex-wrap align-center pa-4 pt-3 pb-2">
        <v-chip
          class="mr-2 mb-2"
          :color="quickFilter === 'all' ? 'primary' : ''"
          :text-color="quickFilter === 'all' ? 'white' : ''"
          @click="applyQuickFilter('all')"
        >
          {{ 'smartQuoteFilterAll'|localize }}
        </v-chip>
        <v-chip
          outlined
          class="mr-2 mb-2"
          :class="{'smart-quote-chip-active': quickFilter === 'needsReview'}"
          @click="applyQuickFilter('needsReview')"
        >
          <span class="smart-quote-dot smart-quote-dot--warning mr-2" />{{ 'smartQuoteStatusProcessedNeedsReview'|localize }}
        </v-chip>
        <v-chip
          outlined
          class="mr-2 mb-2"
          :class="{'smart-quote-chip-active': quickFilter === 'stuck'}"
          @click="applyQuickFilter('stuck')"
        >
          <span class="smart-quote-dot smart-quote-dot--black mr-2" />{{ 'smartQuoteStatStuck'|localize }}
        </v-chip>
        <v-chip
          outlined
          class="mr-2 mb-2"
          :class="{'smart-quote-chip-active': quickFilter === 'failed'}"
          @click="applyQuickFilter('failed')"
        >
          <span class="smart-quote-dot smart-quote-dot--error mr-2" />{{ 'smartQuoteStatusFailed'|localize }}
        </v-chip>
      </div>

      <v-data-table
        class="smart-quote-table"
        :headers="headers"
        :items="items"
        :loading="processingMailbox || loadingList"
        loader-height="2"
        :no-data-text="'noDataAvailable'|localize"
        item-key="id"
        hide-default-footer
        :options.sync="options"
        :server-items-length="total"
        :item-class="rowClass"
        @click:row="openDetails"
      >
        <template v-slot:item.emailDate="{ item }">
          <span class="text-no-wrap">{{ formatTime(item) }}</span>
        </template>
        <template v-slot:item.request="{ item }">
          <router-link
            v-if="item.requestExists && item.numericRequestId"
            class="primary--text"
            style="text-decoration: none"
            target="_blank"
            :to="{name: 'admin-request-details', query: {request_id: item.numericRequestId}}"
            @click.native.stop
          >
            {{ item.requestId }}
          </router-link>
          <v-tooltip v-else-if="item.requestId" bottom color="black">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="grey--text">{{ item.requestId }}</span>
            </template>
            <span>{{ 'smartQuoteRequestNotFound'|localize }}</span>
          </v-tooltip>
          <span v-else class="grey--text">—</span>
        </template>
        <template v-slot:item.carrier="{ item }">
          <v-tooltip top color="black" max-width="320">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="d-block smart-quote-cell-ellipsis" :class="{'grey--text': !item.carrierName}">
                {{ carrierLabel(item) }}
              </span>
            </template>
            <span>{{ carrierLabel(item) }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.attachmentFile="{ item }">
          <v-tooltip v-if="item.attachmentFilename" top color="black" max-width="320">
            <template v-slot:activator="{ on, attrs }">
              <a
                href="#"
                class="smart-quote-attachment-link"
                v-bind="attrs"
                v-on="on"
                @click.prevent.stop="openPdf(item)"
              >
                <v-icon size="16" left>mdi-paperclip</v-icon>
                <span class="smart-quote-cell-ellipsis">{{ item.attachmentFilename }}</span>
                <v-progress-circular v-if="pdfLoadingIds[item.id]" indeterminate size="14" width="2" class="ml-1" />
              </a>
            </template>
            <span>{{ item.attachmentFilename }}</span>
          </v-tooltip>
          <span v-else class="grey--text">
            <v-icon size="16" left color="grey">mdi-file-outline</v-icon>{{ 'smartQuoteNoPdf'|localize }}
          </span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-tooltip v-if="statusTooltip(item)" top color="black" max-width="360">
            <template v-slot:activator="{ on, attrs }">
              <v-chip v-bind="attrs" v-on="on" small :color="statusMeta(item).color" text-color="white">
                {{ statusMeta(item).label }}
              </v-chip>
            </template>
            <span>{{ statusTooltip(item) }}</span>
          </v-tooltip>
          <v-chip v-else small :color="statusMeta(item).color" text-color="white">
            {{ statusMeta(item).label }}
          </v-chip>
        </template>
        <template v-slot:item.lastStep="{ item }">
          <v-tooltip top color="black" max-width="320">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="d-block smart-quote-cell-ellipsis">{{ lastStepText(item) }}</span>
            </template>
            <span>{{ lastStepText(item) }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center justify-end smart-quote-actions" @click.stop>
            <v-btn
              small
              outlined
              color="primary"
              :loading="isLoading(item, primaryAction(item).loadingKey)"
              :disabled="primaryActionDisabled(item)"
              @click="primaryAction(item).handler()"
            >
              {{ primaryAction(item).labelKey|localize }}
            </v-btn>

            <v-menu left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item link @click="openDetails(item)">
                  <v-list-item-title>{{ 'smartQuoteAuditTrail'|localize }}</v-list-item-title>
                </v-list-item>
                <!-- A dismissed or skipped record is read-only: remembering its sender would remap
                     and re-run creation on it, which the backend refuses (422). -->
                <template v-if="item.status !== 'dismissed' && item.status !== 'skipped'">
                  <v-list-item v-if="senderAlreadyRemembered(item)" disabled>
                    <v-list-item-title>{{ 'smartQuoteSenderAlreadyRemembered'|localize }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-else link @click="onRememberSenderClick(item)">
                    <v-list-item-title>{{ 'smartQuoteRememberSender'|localize }}</v-list-item-title>
                  </v-list-item>
                </template>
                <v-list-item link @click="openEmail(item)">
                  <v-list-item-title>{{ 'smartQuoteActionOpenEmail'|localize }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table>

      <v-container class="py-6">
        <v-row align="center">
          <v-col cols="4">
            <span class="text-caption grey--text">{{ footerText }}</span>
          </v-col>
          <v-col cols="4">
            <v-pagination :value="options.page" @input="onPageChange" :length="pageCount" :total-visible="5" color="primary" />
          </v-col>
          <v-col cols="4">
            <div class="d-flex align-center justify-end">
              <v-select
                :value="options.itemsPerPage"
                @change="onItemsPerPageChange"
                :items="perPageOptions"
                dense
                hide-details
                outlined
                class="smart-quote-per-page"
              />
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <v-navigation-drawer v-model="detailsDrawer" right temporary fixed width="400" class="smart-quote-drawer">
      <div class="pa-4" v-if="selectedItem">
        <div class="d-flex align-center justify-space-between mb-4">
          <span class="text-h6">{{ 'smartQuoteDetailsTitle'|localize }}</span>
          <v-btn icon @click="detailsDrawer = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-simple-table dense class="smart-quote-detail-fields mb-4">
          <tbody>
            <tr>
              <td class="text-caption grey--text">{{ 'smartQuoteRequestId'|localize }}</td>
              <td>
                <!-- New tab, like every other request link in the admin area (AdminQuotes, the
                     report tables): the Monitor keeps its filters, page and open panel. -->
                <router-link
                  v-if="selectedItem.requestExists && selectedItem.numericRequestId"
                  class="primary--text"
                  style="text-decoration: none"
                  target="_blank"
                  :to="{name: 'admin-request-details', query: {request_id: selectedItem.numericRequestId}}"
                >
                  {{ selectedItem.requestId }}
                </router-link>
                <span v-else-if="selectedItem.requestId" class="grey--text">{{ selectedItem.requestId }}</span>
                <span v-else class="grey--text">—</span>
              </td>
            </tr>
            <tr>
              <td class="text-caption grey--text">{{ 'smartQuoteColumnCarrier'|localize }}</td>
              <td>{{ carrierLabel(selectedItem) }}</td>
            </tr>
            <tr>
              <td class="text-caption grey--text">{{ (detailCreatedBy ? 'smartQuoteDetailReceivedFrom' : 'smartQuoteDetailEmail')|localize }}</td>
              <td>
                <!-- Opens the source message in Gmail (the panel's former "Open email" button, which
                     the Dismiss action took over); falls back to a plain mailto when the record has
                     no Gmail message id. -->
                <v-tooltip v-if="selectedItem.gmailMessageId" bottom color="black">
                  <template #activator="{on, attrs}">
                    <a v-bind="attrs" v-on="on" href="#" @click.prevent="openEmail(selectedItem)">
                      {{ selectedItem.senderEmail }}
                    </a>
                  </template>
                  <span>{{ 'smartQuoteActionOpenEmail'|localize }}</span>
                </v-tooltip>
                <a v-else :href="'mailto:' + selectedItem.senderEmail">{{ selectedItem.senderEmail }}</a>
              </td>
            </tr>
            <!-- 581: the carrier the quote is created under, only when it differs from the sender —
                 the quote emails in from one address but is created under another carrier user. -->
            <tr v-if="detailCreatedBy">
              <td class="text-caption grey--text">{{ 'smartQuoteDetailCreatedBy'|localize }}</td>
              <td>{{ detailCreatedBy }}</td>
            </tr>
            <tr>
              <td class="text-caption grey--text">{{ 'attachment'|localize }}</td>
              <td>
                <a
                  v-if="selectedItem.attachmentFilename"
                  href="#"
                  @click.prevent="openPdf(selectedItem)"
                >
                  {{ selectedItem.attachmentFilename }}
                </a>
                <span v-else class="grey--text">{{ 'smartQuoteNoPdf'|localize }}</span>
              </td>
            </tr>
            <tr>
              <td class="text-caption grey--text">{{ 'smartQuoteDetailCurrentStatus'|localize }}</td>
              <td><v-chip small :color="statusMeta(selectedItem).color" text-color="white">{{ statusMeta(selectedItem).label }}</v-chip></td>
            </tr>
            <tr v-if="selectedItem.reasonCode || selectedItem.error">
              <td class="text-caption grey--text">{{ 'smartQuoteDetailError'|localize }}</td>
              <td class="error--text">{{ reasonText(selectedItem) || selectedItem.error }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <div v-if="detailLoading" class="d-flex justify-center py-4">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else-if="selectedItem.docupipe" class="mb-4">
          <div class="text-subtitle-2 mb-1">{{ 'smartQuoteQuoteData'|localize }}</div>

          <div class="text-overline grey--text mb-1">{{ 'smartQuoteParamsRequired'|localize }}</div>
          <v-simple-table dense class="smart-quote-kv mb-3">
            <thead>
              <tr>
                <th class="text-left text-caption grey--text">{{ 'smartQuoteColParameter'|localize }}</th>
                <th class="text-left text-caption grey--text">{{ 'smartQuoteColCurrent'|localize }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in requiredRows"
                :key="row.key"
                :class="{'smart-quote-param-invalid': !row.ok}"
              >
                <td class="text-caption">
                  <v-icon size="16" :color="row.ok ? 'success' : 'error'" class="mr-1 mt-n1">
                    {{ row.ok ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
                  </v-icon>
                  {{ row.label }}
                </td>
                <td class="font-weight-medium" :class="{'error--text': !row.ok}">
                  <v-tooltip v-if="(!row.ok || row.hintAlways) && row.hint" bottom max-width="320">
                    <template #activator="{on, attrs}">
                      <span v-bind="attrs" v-on="on" class="smart-quote-hintable">{{ row.extracted }}</span>
                    </template>
                    <span>{{ row.hint }}</span>
                  </v-tooltip>
                  <span v-else>{{ row.extracted }}</span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="text-overline grey--text mb-1">{{ 'smartQuoteParamsOptional'|localize }}</div>
          <v-simple-table dense class="smart-quote-kv">
            <tbody>
              <tr v-for="row in optionalParamRows(selectedItem)" :key="row.key">
                <td class="text-caption grey--text">{{ row.label }}</td>
                <td class="font-weight-medium">{{ row.value }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <div v-if="quoteComment(selectedItem)" class="mt-3">
            <div class="text-caption grey--text mb-1">{{ 'smartQuoteFieldComment'|localize }}</div>
            <div class="text-body-2 smart-quote-comment">
              {{ commentExpanded ? quoteComment(selectedItem) : truncate(quoteComment(selectedItem), 120) }}
            </div>
            <a
              v-if="quoteComment(selectedItem).length > 120"
              class="text-caption primary--text"
              @click.prevent="commentExpanded = !commentExpanded"
            >{{ (commentExpanded ? 'smartQuoteShowLess' : 'smartQuoteShowMore')|localize }}</a>
          </div>
        </div>
        <!-- A skipped record was short-circuited before DocuPipe: there is no quote data to show and
             nothing to process, so the reason (status chip + error row above) is the whole story. -->
        <div v-else-if="selectedItem.status !== 'skipped'" class="grey--text text-body-2 mb-4">{{ 'smartQuoteProcessAttachmentHint'|localize }}</div>

        <div class="d-flex smart-quote-drawer-actions">
          <v-btn
            v-if="panelPrimaryAction(selectedItem)"
            class="flex-grow-1"
            color="primary"
            depressed
            :loading="isLoading(selectedItem, panelPrimaryAction(selectedItem).loadingKey)"
            @click="panelPrimaryAction(selectedItem).handler()"
          >
            {{ panelPrimaryAction(selectedItem).labelKey|localize }}
          </v-btn>
          <v-btn
            v-if="canDismiss(selectedItem)"
            class="flex-grow-1"
            outlined
            color="error"
            :loading="isLoading(selectedItem, 'dismissingIds')"
            @click="openDismissDialog(selectedItem)"
          >
            {{ 'smartQuoteActionDismiss'|localize }}
          </v-btn>
        </div>
        <div v-if="selectedItem.status === 'dismissed'" class="text-caption grey--text mt-2">
          {{ 'smartQuoteDismissedHint'|localize }}
        </div>

        <div class="text-subtitle-2 mt-6 mb-2">{{ 'smartQuoteTimeline'|localize }}</div>
        <div v-if="auditLoading" class="d-flex justify-center py-4">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else-if="!auditEntries.length" class="grey--text text-body-2 pb-2">
          {{ 'smartQuoteAuditEmpty'|localize }}
        </div>
        <v-timeline v-else dense align-top class="smart-quote-timeline mb-2">
          <v-timeline-item
            v-for="entry in auditEntries"
            :key="entry.id"
            small
            fill-dot
            :color="auditMeta(entry.action).color"
          >
            <template v-slot:icon>
              <v-icon color="white" size="14">{{ auditMeta(entry.action).icon }}</v-icon>
            </template>
            <div class="d-flex">
              <div class="text-caption grey--text smart-quote-timeline-time">{{ formatEntryTime(entry.createdAt) }}</div>
              <div class="smart-quote-timeline-content">
                <div class="font-weight-medium text-body-2">{{ actionLabel(entry.action) }}</div>
                <div v-if="entryDetail(entry)" class="text-caption grey--text">{{ entryDetail(entry) }}</div>
              </div>
            </div>
          </v-timeline-item>
        </v-timeline>
      </div>
    </v-navigation-drawer>

    <v-dialog v-model="rememberSenderDialog" width="480" persistent>
      <v-card>
        <v-card-title class="text-h6 d-flex align-center justify-space-between">
          {{ (rememberSenderReassignConflict ? 'smartQuoteRememberSenderReassignTitle' : 'smartQuoteRememberSender')|localize }}
          <v-btn icon @click="closeRememberSenderDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div class="text-body-2 grey--text mb-4">{{ 'smartQuoteRememberSenderDialogHint'|localize }}</div>

          <v-alert v-if="rememberSenderRequestMissing" type="warning" dense text class="text-body-2 mb-4">
            {{ interpolate('smartQuoteRememberSenderNoRequest', {requestId: rememberSenderItem.requestId || '—'}) }}
          </v-alert>

          <v-alert v-else-if="rememberSenderNoCarriers" type="warning" dense text class="text-body-2 mb-4">
            {{ interpolate('smartQuoteRememberSenderNoCarriers', {requestId: rememberSenderItem.requestId || '—'}) }}
          </v-alert>

          <v-alert v-if="rememberSenderReassignConflict" type="warning" dense text class="text-body-2 mb-4">
            {{ rememberSenderReassignWarningText }}
          </v-alert>

          <v-alert v-if="rememberSenderError" type="error" dense text class="text-body-2 mb-4">
            {{ rememberSenderError }}
          </v-alert>

          <h6 class="text-subtitle-2 mb-2">{{ 'company'|localize }} *</h6>
          <v-autocomplete
            v-model="rememberSenderCompanyId"
            @change="onRememberSenderCompanyChange"
            :items="rememberSenderCompanies"
            :loading="rememberSenderCompaniesLoading"
            :search-input.sync="rememberSenderCompanySearch"
            item-text="title"
            item-value="id"
            outlined
            dense
            hide-no-data
            :disabled="rememberSenderMatchImpossible"
            :placeholder="'search'|localize"
          />

          <h6 class="text-subtitle-2 mb-2">{{ 'smartQuoteSenderAssignUser'|localize }}</h6>
          <v-select
            v-model="rememberSenderUserId"
            :items="rememberSenderUsers"
            item-text="name"
            item-value="id"
            item-disabled="disabled"
            outlined
            dense
            clearable
            :disabled="!rememberSenderCompanyId || rememberSenderMatchImpossible"
            :loading="rememberSenderUsersLoading"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }} ({{ item.email }})</v-list-item-title>
                <v-list-item-subtitle v-if="item.disabled" class="text-caption error--text">
                  {{ 'smartQuoteSenderUserNoRelevancy'|localize }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn text :disabled="isLoading(rememberSenderItem, 'rememberingSenderIds')" @click="closeRememberSenderDialog">
            {{ 'cancel'|localize }}
          </v-btn>
          <v-btn
            v-if="rememberSenderReassignConflict"
            color="warning"
            depressed
            :loading="isLoading(rememberSenderItem, 'rememberingSenderIds')"
            @click="confirmReassignSenderDialog"
          >
            {{ 'smartQuoteRememberSenderReassignConfirm'|localize }}
          </v-btn>
          <v-btn
            v-else
            color="primary"
            depressed
            :disabled="!rememberSenderCompanyId || rememberSenderMatchImpossible"
            :loading="isLoading(rememberSenderItem, 'rememberingSenderIds')"
            @click="confirmRememberSenderDialog"
          >
            {{ 'smartQuoteRememberSender'|localize }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="createProposalDialog" width="460" persistent>
      <v-card v-if="createProposalItem">
        <v-card-title class="text-h6 d-flex align-center justify-space-between">
          {{ 'smartQuoteCreateProposal'|localize }}
          <v-btn icon @click="closeCreateProposalDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div class="text-body-2 grey--text mb-3">{{ 'smartQuoteCreateProposalDraftHint'|localize }}</div>

          <div class="d-flex align-center justify-space-between mb-4 pa-2 smart-quote-form-carrier">
            <div class="text-body-2">
              <span class="grey--text">{{ 'smartQuoteColumnCarrier'|localize }}: </span>
              <!-- This badge is deliberately NOT carrierLabel(): it shows match STATE (matched/not,
                   success/error color), not a plain "name or sender email" display, so the fallback
                   here is a generic "Matched" string, never the sender email. -->
              <template v-if="createProposalCarrierMatched">
                <span class="success--text font-weight-medium">{{ createProposalItem.carrierName || ('smartQuoteValueMatched'|localize) }}</span>
              </template>
              <template v-else>
                <!-- Show the mapped carrier even when it isn't matched for this request, so the admin
                     sees who is currently tied to the sender, not just "Not matched". -->
                <span v-if="createProposalItem.carrierName" class="font-weight-medium">{{ createProposalItem.carrierName }} — </span>
                <span class="error--text font-weight-medium">{{ 'smartQuoteValueNotMatched'|localize }}</span>
              </template>
            </div>
            <v-btn
              v-if="!createProposalCarrierMatched"
              small
              text
              color="primary"
              class="ml-2 flex-shrink-0"
              @click="openRememberSenderDialog(createProposalItem)"
            >
              {{ 'smartQuoteMatchCarrier'|localize }}
            </v-btn>
          </div>

          <!-- Carrier user the quote is created under. Defaults to the auto-matched user; editable so
               a matched company whose default user's relevancy doesn't cover this request can still be
               quoted under a relevant colleague. Shown only when a concrete carrier USER was matched
               (a company-level-only match, carrierUserId null, still needs "Remember sender" first, so
               the backend would reject an override there); users without relevancy are disabled. -->
          <template v-if="createProposalCarrierMatched && createProposalMatchedUserId">
            <h6 class="text-subtitle-2 mb-1">{{ 'smartQuoteCreateProposalCompanyUser'|localize }}</h6>
            <v-select
              v-model="createProposalUserId"
              :items="createProposalUsers"
              item-text="name"
              item-value="id"
              item-disabled="disabled"
              outlined
              dense
              class="mb-2"
              :loading="createProposalUsersLoading"
              :error-messages="createProposalErrors.carrier_user_id"
              @change="clearCreateProposalError('carrier_user_id')"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }} ({{ item.email }})</v-list-item-title>
                  <v-list-item-subtitle v-if="item.disabled" class="text-caption error--text">
                    {{ 'smartQuoteSenderUserNoRelevancy'|localize }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-select>
          </template>

          <h6 class="text-subtitle-2 mb-1">{{ 'transportMode'|localize }} *</h6>
          <v-select
            v-model="createProposalForm.transportMode"
            :items="transportModeList"
            item-text="name"
            item-value="value"
            outlined
            dense
            class="mb-2"
            :error-messages="transportModeFieldErrors"
            @input="clearCreateProposalError('transport_mode')"
          />

          <v-row dense>
            <v-col cols="6">
              <h6 class="text-subtitle-2 mb-1">{{ 'smartQuoteFieldCurrency'|localize }} *</h6>
              <!-- v-select over the allowed list, same as CreateQuote/RequestForm (was a v-combobox). -->
              <v-select
                v-model="createProposalForm.currency"
                :items="smartQuoteCurrencyList"
                outlined
                dense
                class="mb-2"
                :error-messages="currencyFieldErrors"
                @change="clearCreateProposalError('currency')"
              />
            </v-col>
            <v-col cols="6">
              <h6 class="text-subtitle-2 mb-1">{{ 'smartQuoteFieldPrice'|localize }} *</h6>
              <!-- Plain field + v-model.number, the project idiom for numeric inputs (was type="number"). -->
              <v-text-field
                v-model.number="createProposalForm.price"
                outlined
                dense
                class="mb-2"
                :error-messages="priceFieldErrors"
                @input="clearCreateProposalError('price')"
              />
            </v-col>
          </v-row>

          <h6 class="text-subtitle-2 mb-1">{{ 'smartQuoteFieldTransit'|localize }} *</h6>
          <v-text-field
            v-model.number="createProposalForm.transitDays"
            outlined
            dense
            class="mb-2"
            :error-messages="transitFieldErrors"
            @input="clearCreateProposalError('estimated_transit_time_days')"
          />

          <v-row dense>
            <v-col cols="7">
              <h6 class="text-subtitle-2 mb-1">{{ 'smartQuoteFieldValidTo'|localize }} *</h6>
              <!-- Standard v-menu + v-date-picker (same idiom as the Monitor's date-range filter and
                   CreateQuote), instead of the native browser <input type="date">. -->
              <v-menu
                ref="createProposalValidToMenu"
                v-model="createProposalValidToMenu"
                :close-on-content-click="false"
                :return-value.sync="createProposalForm.validToDate"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="createProposalForm.validToDate"
                    readonly
                    outlined
                    dense
                    prepend-inner-icon="mdi-calendar"
                    :error-messages="validToFieldErrors"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="createProposalForm.validToDate"
                  no-title
                  scrollable
                  @input="saveCreateProposalValidTo"
                />
              </v-menu>
            </v-col>
            <v-col cols="5">
              <h6 class="text-subtitle-2 mb-1">{{ 'smartQuoteValidToTime'|localize }}</h6>
              <!-- Half-hour dropdown (methods.intervals()), the app's standard time picker (CreateQuote),
                   instead of the native browser <input type="time">. -->
              <v-select
                v-model="createProposalForm.validToTime"
                :items="timeOptions"
                item-value="key"
                item-text="value"
                outlined
                dense
                prepend-inner-icon="mdi-clock-outline"
                :placeholder="'selectTime'|localize"
                :error-messages="createProposalErrors.valid_to_time"
                @change="clearCreateProposalError('valid_to_time')"
              />
            </v-col>
          </v-row>

          <div v-if="!createProposalCarrierMatched" class="text-caption grey--text mt-1">
            {{ 'smartQuoteCreateProposalNeedsCarrier'|localize }}
          </div>

          <!-- Relevancy is a HARD block (581 §2.7): the carrier user's relevancy doesn't cover this
               request, so the quote cannot be created for them — the submit is disabled, not overridable
               (unlike price below). -->
          <v-alert v-if="createProposalRelevancyMismatch" type="error" dense text class="text-body-2 mt-3 mb-0">
            {{ 'smartQuoteCreateProposalRelevancyBlock'|localize }}
          </v-alert>
          <!-- Price over the request limit stays an OVERRIDABLE warning — the admin may still create. -->
          <v-alert v-else-if="createProposalPriceOverLimit" type="warning" dense text class="text-body-2 mt-3 mb-0">
            <span class="font-weight-medium">{{ 'smartQuoteCreateProposalOverrideHint'|localize }}</span>
            {{ createProposalOverrideWarning }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn text :disabled="isLoading(createProposalItem, 'creatingProposalIds')" @click="closeCreateProposalDialog">
            {{ 'cancel'|localize }}
          </v-btn>
          <v-btn
            color="primary"
            depressed
            :disabled="createProposalFormInvalid || createProposalRelevancyMismatch"
            :loading="isLoading(createProposalItem, 'creatingProposalIds')"
            @click="confirmCreateProposalDialog"
          >
            {{ 'smartQuoteCreateProposal'|localize }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dismissDialog" width="420" persistent>
      <v-card>
        <v-card-title class="text-h6">{{ 'smartQuoteDismissTitle'|localize }}</v-card-title>
        <v-card-text class="text-body-2">
          <div>{{ 'smartQuoteDismissConfirm'|localize }}</div>
          <div v-if="dismissItem" class="mt-2 grey--text">
            {{ dismissItem.requestId }} — {{ carrierLabel(dismissItem) }}
          </div>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn text :disabled="isLoading(dismissItem, 'dismissingIds')" @click="closeDismissDialog">
            {{ 'cancel'|localize }}
          </v-btn>
          <v-btn
            color="error"
            depressed
            :loading="isLoading(dismissItem, 'dismissingIds')"
            @click="confirmDismiss"
          >
            {{ 'smartQuoteActionDismiss'|localize }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import localize from '@/filters/localize';
  import methods from '@/helpers/methods';
  import api from '@/api/api';

  const STATUS_META = {
    fetched: {labelKey: 'smartQuoteStatusFetched', color: 'info'},
    processed_valid: {labelKey: 'smartQuoteStatusProcessedValid', color: 'teal'},
    processed_needs_review: {labelKey: 'smartQuoteStatusProcessedNeedsReview', color: 'warning'},
    proposal_created: {labelKey: 'smartQuoteStatusProposalCreated', color: 'success'},
    duplicate_proposal: {labelKey: 'smartQuoteStatusDuplicateProposal', color: 'blue-grey'},
    blocked_for_creation: {labelKey: 'smartQuoteStatusBlockedForCreation', color: 'warning'},
    // An email carried more than one PDF attachment: no proposal is auto-created, the record needs
    // manual review to pick/split the right attachment (same "needs attention" family as
    // processed_needs_review/blocked_for_creation, so it gets the same 'warning' color).
    multiple_attachments: {labelKey: 'smartQuoteStatusMultipleAttachments', color: 'warning'},
    // Feature 581 "Skipped": request no longer open for quoting (booked/closed/cancelled or past
    // its quotation deadline). Terminal & read-only; the reason is in item.error.
    skipped: {labelKey: 'smartQuoteStatusSkipped', color: 'blue-grey darken-1'},
    dismissed: {labelKey: 'smartQuoteStatusDismissed', color: 'grey darken-1'},
  };

  // 581 Group A structured reasons: reasonCode (snake_case, from the backend) -> i18n key.
  // Only the "no longer needs a raw error string" statuses (multiple_attachments, skipped,
  // blocked_for_creation) get a reasonCode; failed_* (Group C) has none and falls back to item.error.
  const REASON_CODE_KEYS = {
    multiple_attachments: 'smartQuoteReasonMultipleAttachments',
    request_booked: 'smartQuoteReasonRequestBooked',
    request_closed: 'smartQuoteReasonRequestClosed',
    request_cancelled: 'smartQuoteReasonRequestCancelled',
    deadline_passed: 'smartQuoteReasonDeadlinePassed',
    transport_mode_missing: 'smartQuoteReasonTransportModeMissing',
    price_missing: 'smartQuoteReasonPriceMissing',
    price_decimal_not_allowed: 'smartQuoteReasonPriceDecimalNotAllowed',
  };

  // 581 Group B structured criterion hints: matchDetails.<criterion>.code (snake_case, from the
  // backend) -> i18n key for the "?" tooltip in the required-params table. Falls back to the
  // criterion's raw `comment` (old records written before the backend sent code/params) — see
  // criterionHint().
  const CRITERION_HINT_KEYS = {
    validation_passed: 'smartQuoteHintValidationPassed',
    validation_failed: 'smartQuoteHintValidationFailed',
    carrier_identified: 'smartQuoteHintCarrierIdentified',
    carrier_not_identified: 'smartQuoteHintCarrierNotIdentified',
    mode_no_request_mode: 'smartQuoteHintModeNoRequestMode',
    mode_pdf_missing: 'smartQuoteHintModePdfMissing',
    mode_matches_single: 'smartQuoteHintModeMatchesSingle',
    mode_matches_multiple: 'smartQuoteHintModeMatchesMultiple',
    mode_mismatch: 'smartQuoteHintModeMismatch',
    price_no_assignment: 'smartQuoteHintPriceNoAssignment',
    price_currency_mismatch: 'smartQuoteHintPriceCurrencyMismatch',
    price_no_limit: 'smartQuoteHintPriceNoLimit',
    price_not_numeric: 'smartQuoteHintPriceNotNumeric',
    price_within_limit: 'smartQuoteHintPriceWithinLimit',
    price_exceeds_limit: 'smartQuoteHintPriceExceedsLimit',
    relevancy_no_user: 'smartQuoteHintRelevancyNoUser',
    relevancy_no_valid: 'smartQuoteHintRelevancyNoValid',
    relevancy_request_not_found: 'smartQuoteHintRelevancyRequestNotFound',
    relevancy_request_incomplete: 'smartQuoteHintRelevancyRequestIncomplete',
    relevancy_matches: 'smartQuoteHintRelevancyMatches',
    relevancy_not_evaluated: 'smartQuoteHintRelevancyNotEvaluated',
    relevancy_mismatch: 'smartQuoteHintRelevancyMismatch',
  };

  // Transport-mode tokens as sent by the backend (docupipe raw mode / request transport modes) —
  // localized via the same 'sea'/'road'/'air'/'rail' i18n keys used elsewhere. See modeLabel().
  const TRANSPORT_MODE_TOKENS = ['sea', 'road', 'air', 'rail'];

  const ACTION_LABEL_KEYS = {
    fetched: 'smartQuoteActionFetched',
    docupipe_extracted: 'smartQuoteActionDocupipeExtracted',
    processed: 'smartQuoteActionProcessed',
    skipped: 'smartQuoteActionSkipped',
    multiple_attachments: 'smartQuoteActionMultipleAttachments',
    processing_failed: 'smartQuoteActionProcessingFailed',
    proposal_created: 'smartQuoteActionProposalCreated',
    creation_skipped_duplicate: 'smartQuoteActionCreationSkippedDuplicate',
    creation_blocked: 'smartQuoteActionCreationBlocked',
    creation_failed: 'smartQuoteActionCreationFailed',
    dismissed: 'smartQuoteActionDismissed',
    sender_remembered: 'smartQuoteActionSenderRemembered',
    sender_updated: 'smartQuoteActionSenderUpdated',
    sender_enabled: 'smartQuoteActionSenderEnabled',
    sender_disabled: 'smartQuoteActionSenderDisabled',
    sender_deleted: 'smartQuoteActionSenderDeleted',
    push_received: 'smartQuoteActionPushReceived',
    watch_renewed: 'smartQuoteActionWatchRenewed',
    sync_failed: 'smartQuoteActionSyncFailed',
    notification_sent: 'smartQuoteActionNotificationSent',
  };

  const AUDIT_META = {
    fetched: {icon: 'mdi-email-outline', color: 'info'},
    docupipe_extracted: {icon: 'mdi-download', color: 'info'},
    processed: {icon: 'mdi-check', color: 'teal'},
    skipped: {icon: 'mdi-debug-step-over', color: 'blue-grey darken-1'},
    multiple_attachments: {icon: 'mdi-file-multiple', color: 'warning'},
    proposal_created: {icon: 'mdi-check-circle', color: 'success'},
    creation_skipped_duplicate: {icon: 'mdi-content-duplicate', color: 'blue-grey'},
    creation_failed: {icon: 'mdi-close-circle', color: 'error'},
    processing_failed: {icon: 'mdi-close-circle', color: 'error'},
    sync_failed: {icon: 'mdi-close-circle', color: 'error'},
    creation_blocked: {icon: 'mdi-alert', color: 'warning'},
    dismissed: {icon: 'mdi-cancel', color: 'grey darken-1'},
    sender_remembered: {icon: 'mdi-bookmark', color: 'primary'},
    sender_updated: {icon: 'mdi-pencil', color: 'grey'},
    sender_enabled: {icon: 'mdi-check', color: 'grey'},
    sender_disabled: {icon: 'mdi-close', color: 'grey'},
    sender_deleted: {icon: 'mdi-delete', color: 'grey'},
    notification_sent: {icon: 'mdi-bell', color: 'info'},
    push_received: {icon: 'mdi-arrow-down-bold', color: 'grey'},
    watch_renewed: {icon: 'mdi-refresh', color: 'grey'},
  };

  // Server-side whitelist for the `sort_by` query param (GET /admin/smart-quote/attachments).
  // Anything the table produces outside this list falls back to `email_date`.
  const SORT_WHITELIST = [
    'email_date', 'processed_at', 'proposal_created_at', 'status',
    'match_percent', 'attachment_size', 'sender_email', 'request_id', 'id',
  ];
  const SORT_FIELD_MAP = {
    emailDate: 'email_date',
  };

  export default {
    name: 'SmartQuoteMonitor',

    data() {
      return {
        processingMailbox: false,
        loadingList: false,
        items: [],
        total: 0,
        stats: {processedToday: 0, createdToday: 0, needsReview: 0, failed: 0, stuck: 0},
        processingAttachmentIds: {},
        creatingProposalIds: {},
        rememberingSenderIds: {},
        dismissingIds: {},
        pdfLoadingIds: {},
        commentExpanded: false,

        dismissDialog: false,
        dismissItem: null,

        dateRange: [],
        dateMenu: false,
        statusFilter: '',
        debouncedCarrierFilter: '',
        carrierFilterTimeout: null,
        debouncedRequestIdSearch: '',
        requestIdSearchTimeout: null,
        quickFilter: 'all',

        options: {page: 1, itemsPerPage: 20, sortBy: ['emailDate'], sortDesc: [true]},

        detailsDrawer: false,
        selectedItem: null,
        detailLoading: false,
        auditEntries: [],
        auditLoading: false,

        rememberSenderDialog: false,
        rememberSenderItem: null,
        rememberSenderError: null,
        rememberSenderReassignConflict: null,
        rememberSenderCompanyId: null,
        rememberSenderUserId: null,
        rememberSenderPreselectUserId: null,
        rememberSenderCompanySearch: '',
        rememberSenderCompanySearchTimeout: null,
        rememberSenderCompanies: [],
        rememberSenderCompaniesLoading: false,
        rememberSenderUsers: [],
        rememberSenderUsersLoading: false,

        createProposalDialog: false,
        createProposalValidToMenu: false,
        createProposalItem: null,
        createProposalForm: {transportMode: null, currency: null, price: null, transitDays: null, validToDate: null, validToTime: null},
        createProposalErrors: {},
        // Carrier user the quote will be created under. Defaults to the auto-matched user; the admin
        // can pick another user of the SAME matched company (e.g. when the default one's relevancy
        // does not cover the request). createProposalUsers is that company's roster with per-user
        // relevantToRequest (irrelevant ones are disabled in the select).
        createProposalUserId: null,
        createProposalUsers: [],
        createProposalUsersLoading: false,
      };
    },

    watch: {
      options: {
        handler() {
          this.loadAttachments();
        },
        deep: true,
      },
      carrierFilter() {
        this.resetToFirstPage();
      },
      requestIdSearch() {
        this.resetToFirstPage();
      },
      dateRange() {
        this.resetToFirstPage();
      },
      rememberSenderCompanySearch() {
        if (!this.rememberSenderDialog) return;
        if (this.rememberSenderCompanySearchTimeout) clearTimeout(this.rememberSenderCompanySearchTimeout);
        this.rememberSenderCompanySearchTimeout = setTimeout(() => this.searchRememberSenderCompanies(), 400);
      },
    },

    computed: {
      headers() {
        return [
          {text: localize('smartQuoteColumnTime'), value: 'emailDate', sortable: true, mustSort: true, width: 135},
          {text: localize('smartQuoteRequestId'), value: 'request', sortable: false, width: 110},
          {text: localize('smartQuoteColumnCarrier'), value: 'carrier', sortable: false, width: 170},
          {text: localize('attachment'), value: 'attachmentFile', sortable: false, width: 200},
          {text: localize('status'), value: 'status', sortable: false, align: 'center', width: 140},
          {text: localize('smartQuoteColumnLastStep'), value: 'lastStep', sortable: false},
          {text: '', value: 'actions', sortable: false, align: 'end', width: 190},
        ];
      },
      statCards() {
        return [
          {key: 'processedToday', variant: 'info', icon: 'mdi-file-document-outline', label: localize('smartQuoteStatProcessedToday'), value: this.stats.processedToday, hint: localize('smartQuoteStatProcessedTodayHint')},
          {key: 'created', variant: 'success', icon: 'mdi-check-circle-outline', label: localize('smartQuoteStatCreated'), value: this.stats.createdToday, hint: localize('smartQuoteStatCreatedHint')},
          {key: 'needsReview', variant: 'warning', icon: 'mdi-clock-alert-outline', label: localize('smartQuoteStatNeedsReview'), value: this.stats.needsReview, hint: localize('smartQuoteStatNeedsReviewHint')},
          {key: 'stuck', variant: 'error', icon: 'mdi-alert-outline', label: localize('smartQuoteStatStuck'), value: this.stats.stuck, hint: localize('smartQuoteStatStuckHint')},
          {key: 'failed', variant: 'error', icon: 'mdi-close-circle-outline', label: localize('smartQuoteStatFailed'), value: this.stats.failed, hint: localize('smartQuoteStatFailedHint')},
        ];
      },
      statusOptions() {
        let opts = [{value: '', text: localize('smartQuoteFilterAll')}];
        Object.keys(STATUS_META).forEach(key => opts.push({value: key, text: localize(STATUS_META[key].labelKey)}));
        opts.push({value: 'failed', text: localize('smartQuoteStatusFailed')});
        return opts;
      },
      perPageOptions() {
        return [10, 20, 25, 50, 100].map(n => ({value: n, text: `${n} ${localize('smartQuotePerPageSuffix')}`}));
      },
      transportModeList() {
        return this.$store.getters.transportModeList;
      },
      // Suggestions for the create-proposal currency field. The backend
      // (config('smart_quote.allowed_currencies')) is the source of truth and validates on submit;
      // the combobox still accepts a typed value so a PDF currency outside the list stays editable.
      smartQuoteCurrencyList() {
        return ['SEK', 'EUR', 'USD'];
      },
      // The app's shared half-hour time options ({key, value}, '' = any time) — used by the valid-to
      // time picker so it matches CreateQuote's dropdown instead of a native <input type="time">.
      timeOptions() {
        return methods.intervals();
      },
      requiredRows() {
        return this.selectedItem ? this.requiredParamRows(this.selectedItem) : [];
      },
      // The carrier USER the quote would be created under — the carrier user's email, shown in the
      // detail panel only when it differs from the sender that emailed the quote in (581): "received
      // from X, created by Y". It is strictly the user email (carrierUserEmail); it never falls back
      // to the company name — "created by <company>" would be misleading, since a company is not who
      // the proposal is created as. Null (row hidden) when there is no matched carrier user, or it
      // equals the sender — then the panel keeps the single "Email" row.
      detailCreatedBy() {
        let it = this.selectedItem;
        if (!it) return null;
        let value = it.carrierUserEmail || null;
        return (value && value !== it.senderEmail) ? value : null;
      },
      // Whether the record being edited has a matched carrier — gates "Create proposal" submit while
      // leaving the rest of the form editable (values can be filled before the carrier is matched).
      createProposalCarrierMatched() {
        return this.carrierMatched(this.createProposalItem);
      },
      // Every transport mode the request expects from the matched carrier. A request can be quoted
      // for several modes (one transport_company_user_request row each), so this is a list and any
      // of them is a valid choice. Empty until a carrier is matched — the backend enforces the
      // match either way. `transportMode` (singular) is the legacy fallback for older records.
      createProposalExpectedModes() {
        let m = this.createProposalItem && this.createProposalItem.docupipe && this.createProposalItem.docupipe.carrierMapping;
        if (!m) return [];
        if (Array.isArray(m.transportModes) && m.transportModes.length) return m.transportModes;
        return m.transportMode ? [m.transportMode] : [];
      },
      createProposalModeMismatch() {
        let modes = this.createProposalExpectedModes;
        let picked = this.createProposalForm.transportMode;
        return !!(modes.length && picked && !modes.includes(picked));
      },
      // 581 §2.7: price-over-limit and a full-relevancy mismatch make the sender "not valid" (needs
      // review, Create-proposal inactive from the table), but they DON'T hard-block the manual form —
      // the admin can knowingly override. These surface that override as a warning inside the dialog.
      createProposalPriceOverLimit() {
        return this.priceOverLimit(this.createProposalItem);
      },
      // The carrier company/user auto-resolved for the record being edited (carrier_mapping) — the
      // company whose roster the Company user picker lists, and the user it defaults to.
      createProposalMatchedCompanyId() {
        let m = this.createProposalItem && this.createProposalItem.docupipe && this.createProposalItem.docupipe.carrierMapping;
        return (m && m.companyId) || null;
      },
      createProposalMatchedUserId() {
        let m = this.createProposalItem && this.createProposalItem.docupipe && this.createProposalItem.docupipe.carrierMapping;
        return (m && m.carrierUserId) || null;
      },
      // The carrier user currently picked in the Company user select, resolved against the loaded
      // candidate roster ({id, name, email, relevantToRequest, disabled}).
      createProposalSelectedUser() {
        return this.createProposalUsers.find(u => u.id === this.createProposalUserId) || null;
      },
      createProposalRelevancyMismatch() {
        let item = this.createProposalItem;
        if (!item) return false;
        // With the company's carrier roster loaded, the relevancy block is judged against the
        // SELECTED user (the picker's whole purpose): choosing a colleague whose relevancy covers the
        // request clears the block the auto-picked default raised. Fall back to the record's own
        // matched-user verdict before the roster loads / when there is no picker.
        let selected = this.createProposalSelectedUser;
        if (selected) return selected.relevantToRequest === false;
        return this.relevancyMismatch(item);
      },
      // Price-only override notice (relevancy is a hard block with its own error alert, not overridable).
      createProposalOverrideWarning() {
        if (!this.createProposalPriceOverLimit) return '';
        let md = this.matchDetails(this.createProposalItem);
        return this.criterionHint(md.priceWithinLimit) || localize('smartQuotePriceOverLimit');
      },
      // Live per-field validation for the Create proposal form: each returns the backend 422 message
      // when present, otherwise validates the current input so wrong values are highlighted red
      // immediately (before submit) instead of silently. Empty required fields also show "Required".
      transportModeFieldErrors() {
        if (this.createProposalErrors.transport_mode) return this.createProposalErrors.transport_mode;
        if (!this.createProposalForm.transportMode) return [localize('smartQuoteFieldRequired')];
        // Matched carrier → compare the current selection against the request's modes (clears live
        // when the admin picks one of them).
        if (this.createProposalExpectedModes.length) {
          return this.createProposalModeMismatch
            ? [this.interpolate('smartQuoteTransportModeMismatch', {expected: this.createProposalExpectedModes.join(', ')})]
            : [];
        }
        // No matched carrier to compare against → mirror the required table's stored criterion, so the
        // field is red whenever that row is, with the same reason.
        let tm = this.createProposalCriterion('transportMode');
        return (tm && tm.passed === false) ? [tm.comment || localize('smartQuoteFieldRequired')] : [];
      },
      currencyFieldErrors() {
        if (this.createProposalErrors.currency) return this.createProposalErrors.currency;
        let c = String(this.createProposalForm.currency || '').trim().toUpperCase();
        if (!c) return [localize('smartQuoteFieldRequired')];
        if (!this.smartQuoteCurrencyList.includes(c)) {
          return [this.interpolate('smartQuoteCurrencyNotAllowed', {list: this.smartQuoteCurrencyList.join(', ')})];
        }
        return [];
      },
      priceFieldErrors() {
        if (this.createProposalErrors.price) return this.createProposalErrors.price;
        let p = this.createProposalForm.price;
        if (p === null || p === undefined || p === '') return [localize('smartQuoteFieldRequired')];
        let n = Number(p);
        if (isNaN(n) || n <= 0) return [localize('smartQuotePricePositive')];
        return [];
      },
      transitFieldErrors() {
        if (this.createProposalErrors.estimated_transit_time_days) return this.createProposalErrors.estimated_transit_time_days;
        let t = this.createProposalForm.transitDays;
        if (t === null || t === undefined || t === '') return [localize('smartQuoteFieldRequired')];
        let n = Number(t);
        if (isNaN(n) || n <= 0 || !Number.isInteger(n)) return [localize('smartQuoteTransitPositive')];
        return [];
      },
      validToFieldErrors() {
        if (this.createProposalErrors.valid_to_date) return this.createProposalErrors.valid_to_date;
        if (!this.createProposalForm.validToDate) return [localize('smartQuoteFieldRequired')];
        return [];
      },
      createProposalFormInvalid() {
        return this.transportModeFieldErrors.length > 0
          || this.currencyFieldErrors.length > 0
          || this.priceFieldErrors.length > 0
          || this.transitFieldErrors.length > 0
          || this.validToFieldErrors.length > 0;
      },
      // The request this attachment references doesn't exist in Fraktus at all — no sender mapping
      // can produce a match here. The picker warns and blocks confirm.
      rememberSenderRequestMissing() {
        return !!this.rememberSenderItem && this.rememberSenderItem.requestExists === false;
      },
      // The request exists but has no carriers assigned to it (no transport_company_user_request
      // rows), so there is nothing for a sender mapping to match against here either.
      rememberSenderNoCarriers() {
        let it = this.rememberSenderItem;
        return !!it && it.requestExists !== false && it.requestHasCarriers === false;
      },
      // Either way, matching the carrier for this record is impossible — disable the picker.
      rememberSenderMatchImpossible() {
        return this.rememberSenderRequestMissing || this.rememberSenderNoCarriers;
      },
      // The sender is already mapped to a DIFFERENT company (409 conflict:sender_reassign from the
      // remember-sender endpoint). Rendered as the dialog's warning text once the admin has been
      // asked to confirm the reassignment.
      rememberSenderReassignWarningText() {
        let c = this.rememberSenderReassignConflict;
        if (!c) return '';
        return this.interpolate('smartQuoteRememberSenderReassignWarning', {
          currentCompany: c.currentCompanyName,
          status: localize(c.currentEnabled ? 'smartQuoteStatusActive' : 'smartQuoteStatusDisabled'),
          targetCompany: c.targetCompanyName,
        });
      },
      dateRangeText() {
        if (!this.dateRange.length) return localize('smartQuoteFilterAllTime');
        return [...this.dateRange].sort().join(' ~ ');
      },
      // Quick-filter chips and the status select both ultimately drive a single server-side
      // `status` param. Chips take priority while active. The 'failed' and 'needs_review' buckets
      // are generic values the backend expands to several real statuses (matching the stat cards),
      // so the chip count and the filtered rows stay consistent.
      statusQueryValue() {
        if (this.quickFilter === 'failed') return 'failed';
        if (this.quickFilter === 'stuck') return 'fetched';
        if (this.quickFilter === 'needsReview') return 'needs_review';
        return this.statusFilter || '';
      },
      pageCount() {
        return Math.max(1, Math.ceil(this.total / this.options.itemsPerPage));
      },
      pageStart() {
        return this.total === 0 ? 0 : (this.options.page - 1) * this.options.itemsPerPage + 1;
      },
      pageEnd() {
        return Math.min(this.options.page * this.options.itemsPerPage, this.total);
      },
      footerText() {
        if (!this.total) return localize('smartQuoteFooterNoResults');
        return this.interpolate('smartQuoteFooterSummary', {
          from: this.pageStart,
          to: this.pageEnd,
          total: this.total,
        });
      },
      carrierFilter: {
        get() {
          return this.debouncedCarrierFilter;
        },
        set(val) {
          if (this.carrierFilterTimeout) clearTimeout(this.carrierFilterTimeout);
          this.carrierFilterTimeout = setTimeout(() => {
            this.debouncedCarrierFilter = val;
          }, 400);
        },
      },
      requestIdSearch: {
        get() {
          return this.debouncedRequestIdSearch;
        },
        set(val) {
          if (this.requestIdSearchTimeout) clearTimeout(this.requestIdSearchTimeout);
          this.requestIdSearchTimeout = setTimeout(() => {
            this.debouncedRequestIdSearch = val;
          }, 400);
        },
      },
    },

    created() {
      // The initial `loadAttachments()` call is triggered by the `options` watcher below:
      // v-data-table's VData mixin has an `immediate: true` deep watcher on its internal options
      // that unconditionally emits `update:options` once on creation (this is the standard
      // Vuetify server-side-table pattern), which updates `options` here via `.sync` and fires
      // the watcher. Calling loadAttachments() explicitly here as well would double-fetch page 1.
      this.getStats();
    },

    methods: {
      async loadAttachments() {
        try {
          this.loadingList = true;
          let response = await api.listSmartQuoteAttachments(this.queryData());
          this.items = response.data.items;
          this.total = response.data.total;
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.loadingList = false;
        }
      },
      async getStats() {
        try {
          let response = await api.getSmartQuoteStats();
          this.stats = response.data;
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        }
      },
      queryData() {
        let params = {
          page: this.options.page,
          per_page: this.options.itemsPerPage,
          sort_by: this.sortByParam(),
          sort_desc: this.sortDescParam(),
        };
        if (this.statusQueryValue) params.status = this.statusQueryValue;
        if (this.carrierFilter) params.carrier = this.carrierFilter;
        if (this.requestIdSearch) params.search = this.requestIdSearch;
        if (this.dateRange.length) {
          let [from, to] = this.dateRange.length === 2 ? [...this.dateRange].sort() : [this.dateRange[0], this.dateRange[0]];
          params.date_from = from;
          params.date_to = to;
        }
        return params;
      },
      sortByParam() {
        let field = this.options.sortBy && this.options.sortBy.length ? this.options.sortBy[0] : null;
        let mapped = field ? (SORT_FIELD_MAP[field] || field) : null;
        return mapped && SORT_WHITELIST.includes(mapped) ? mapped : 'email_date';
      },
      sortDescParam() {
        if (this.options.sortDesc && this.options.sortDesc.length && this.options.sortDesc[0] !== undefined) {
          return !!this.options.sortDesc[0];
        }
        return true;
      },
      resetToFirstPage() {
        if (this.options.page === 1) {
          this.loadAttachments();
        } else {
          this.options = {...this.options, page: 1};
        }
      },
      onPageChange(page) {
        this.options = {...this.options, page};
      },
      onItemsPerPageChange(itemsPerPage) {
        this.options = {...this.options, itemsPerPage, page: 1};
      },
      onStatusSelectChange(val) {
        if (val) this.quickFilter = 'all';
        this.resetToFirstPage();
      },
      interpolate(key, params) {
        let str = localize(key);
        Object.keys(params).forEach(param => {
          str = str.replace(`{${param}}`, params[param]);
        });
        return str;
      },
      // 581: render a localized Group A reason from item.reasonCode/reasonParams. Falls back to the
      // raw item.error when reasonCode is absent (old rows written before this feature, and Group C
      // failed_* technical errors, which never get a reasonCode).
      reasonText(item) {
        let key = item.reasonCode && REASON_CODE_KEYS[item.reasonCode];
        if (!key) return item.error;
        let params = {...(item.reasonParams || {})};
        Object.keys(params).forEach(param => {
          if (Array.isArray(params[param])) params[param] = params[param].join(', ');
        });
        return this.interpolate(key, params);
      },
      // 581 Group B: render a localized tooltip hint for a matchDetails.<criterion> entry from its
      // code/params (backend-computed, see the contract in SmartQuoteService's criterion builders).
      // Falls back to the criterion's raw `comment` for old records processed before the backend sent
      // code/params, and to '' when the criterion itself is absent.
      criterionHint(criterion) {
        if (!criterion) return '';
        let key = criterion.code && CRITERION_HINT_KEYS[criterion.code];
        if (!key) return criterion.comment || '';
        let params = {...(criterion.params || {})};
        Object.keys(params).forEach(param => {
          let value = Array.isArray(params[param]) ? params[param].join(', ') : params[param];
          params[param] = (param === 'mode' || param === 'expected') ? this.modeLabel(value) : value;
        });
        return this.interpolate(key, params);
      },
      // Localizes a raw transport-mode token (or a ", "-joined list of them, e.g. request modes)
      // via the existing 'sea'/'road'/'air'/'rail' i18n keys. Unknown tokens pass through unchanged.
      modeLabel(token) {
        if (!token) return token;
        return String(token).split(', ').map(part => {
          let trimmed = part.trim();
          return TRANSPORT_MODE_TOKENS.includes(trimmed.toLowerCase()) ? localize(trimmed.toLowerCase()) : trimmed;
        }).join(', ');
      },
      isToday(iso) {
        if (!iso) return false;
        return methods.formatDate(new Date(iso)) === methods.todayDate();
      },
      formatTime(item) {
        if (!item.emailDate) return '—';
        let date = new Date(item.emailDate);
        let time = date.toTimeString().slice(0, 5);
        return this.isToday(item.emailDate) ? time : `${methods.formatDate(date)} ${time}`;
      },
      formatEntryTime(iso) {
        if (!iso) return '—';
        return new Date(iso).toTimeString().slice(0, 5);
      },
      applyQuickFilter(name) {
        this.quickFilter = name;
        if (name !== 'all') this.statusFilter = '';
        this.resetToFirstPage();
      },
      rowClass(item) {
        return this.selectedItem && this.selectedItem.id === item.id ? 'smart-quote-row-selected' : '';
      },
      isLoading(item, loadingKey) {
        if (!item || !loadingKey) return false;
        return !!this[loadingKey][item.id];
      },
      // --- Single source of truth for reading item.docupipe.matchDetails (581 §2.7) ---
      // The list row, the detail drawer, the create-proposal dialog and the remember-sender success
      // handler all used to re-derive the same matchDetails booleans independently (and, before the
      // backend refactor, sometimes mixed in item.senderMapping too). They now all read through these
      // helpers instead, so a rule change only has to happen in one place.
      matchDetails(item) {
        return (item && item.docupipe && item.docupipe.matchDetails) || {};
      },
      // Carrier resolution comes ONLY from carrier_mapping — matchDetails.carrierMapping.passed.
      // item.senderMapping ("Remember sender") is never mixed into this on the backend anymore, and
      // must not be mixed in here either; it only pre-fills the remember-sender dialog (carrierKnown()).
      carrierMatched(item) {
        let md = this.matchDetails(item);
        return !!(md.carrierMapping && md.carrierMapping.passed);
      },
      // 581 §2.7 hard block: the matched carrier's full relevancy doesn't cover this request.
      relevancyMismatch(item) {
        let md = this.matchDetails(item);
        return !!(md.relevancyMatch && md.relevancyMatch.passed === false);
      },
      // 581 §2.7 overridable warning: the extracted price is above the request's price_limit.
      priceOverLimit(item) {
        let md = this.matchDetails(item);
        return !!(md.priceWithinLimit && md.priceWithinLimit.exceedsLimit);
      },
      // Did the PDF-extracted transport mode match one of the request's required modes. NOTE this is
      // a distinct concept from createProposalExpectedModes (docupipe.carrierMapping.transportModes —
      // the list of modes the MATCHED carrier is assigned to, used to validate the admin's manual
      // dropdown pick) and from createProposalCriterion('transportMode') (the same matchDetails
      // criterion, but read for whichever record is open in the create-proposal dialog). All three
      // ultimately read matchDetails through the matchDetails() helper above.
      transportModePassed(item) {
        let md = this.matchDetails(item);
        return !!(md.transportMode && md.transportMode.passed);
      },
      // Display-only label for "who is this from" — falls back to the raw sender email when no
      // carrier is resolved. Purely cosmetic: never used to decide carrier/match state (carrierMatched
      // above is the only source of truth for that).
      carrierLabel(item) {
        return (item && (item.carrierName || item.senderEmail)) || '';
      },
      // Parameters that MUST be present/valid for a proposal to be created from this quote —
      // the backend's required_fields (validation.missingFields) plus the carrier match and
      // transport-mode match (matchDetails). `ok: false` rows are highlighted red; `hint` carries
      // the "why" (a matchDetails criterion comment or a validation warning) shown on hover.
      requiredParamRows(item) {
        let d = item.docupipe || {};
        let q = d.normalizedQuote || {};
        let missing = (d.validation && d.validation.missingFields) || [];
        let warnings = (d.validation && d.validation.warnings) || [];
        let md = this.matchDetails(item);
        // A required field is "present" when the backend validator did NOT list it in missingFields
        // (which also covers invalid values, e.g. an unsupported currency or a non-positive price).
        let ok = (field) => !missing.includes(field);
        let val = (v) => (v === null || v === undefined || v === '') ? '—' : v;
        // The first validation warning mentioning a keyword, or a generic "missing/invalid" hint.
        let warnHint = (keyword) => {
          let w = warnings.find(warning => warning.toLowerCase().includes(keyword));
          return w || localize('smartQuoteParamMissingHint');
        };
        // Prefer the persisted item-level valid-to fields; fall back to the raw normalized quote.
        let validTo = (item.validToDate || item.validToTime)
          ? [item.validToDate, item.validToTime].filter(Boolean).join(' ')
          : ((q.quoteValidTo && q.quoteValidTo.date)
            ? [q.quoteValidTo.date, q.quoteValidTo.time].filter(Boolean).join(' ')
            : null);

        // Carrier resolution is carrierMatched alone (matchDetails.carrierMapping.passed) — the
        // backend derives carrierName/carrierUserEmail from carrier_mapping only and no longer mixes
        // in item.senderMapping, so neither does this row. The record is always re-matched (on
        // sender-mapping change and on opening this panel), so carrierMatched is always current.
        let carrierMatched = this.carrierMatched(item);
        let relevancyMismatch = this.relevancyMismatch(item);
        let priceOverLimit = this.priceOverLimit(item);
        // 581 §2.7: carrierMapping.passed (carrierMatched above) now only means the carrier COMPANY
        // was recognized — it says nothing about a specific carrier USER, which is what relevancy is
        // actually evaluated against. item.carrierUserEmail (same field detailCreatedBy reads) is the
        // one place a concrete carrier user shows up on the record, so it gates whether the relevancy
        // row has anything real to report.
        let carrierUserKnown = !!item.carrierUserEmail;

        return [
          {
            key: 'requestId',
            label: localize('smartQuoteFieldRequestId'),
            extracted: val(item.requestId || q.requestId),
            ok: !!(item.requestId || q.requestId) && item.requestExists !== false,
            hint: (item.requestId || q.requestId)
              ? localize('smartQuoteParamRequestNotFoundHint')
              : localize('smartQuoteParamMissingHint'),
          },
          {
            key: 'carrier',
            label: localize('smartQuoteCriterionCarrier'),
            extracted: carrierMatched ? item.carrierName : localize('smartQuoteValueNotMatched'),
            ok: carrierMatched,
            hint: this.criterionHint(md.carrierMapping),
          },
          {
            key: 'transportMode',
            label: localize('smartQuoteCriterionMode'),
            extracted: this.modeLabel(val(q.transportMode)),
            ok: this.transportModePassed(item),
            hint: this.criterionHint(md.transportMode),
          },
          {
            // Full-relevancy match of the mapped carrier USER against the request (581 §2.7): red when
            // the relevancy was evaluated and does NOT fully match (route / goods / requirements
            // differ) — this blocks creation. It stays a neutral dash — with a hint pointing at
            // Remember sender — until a specific carrier user is known (carrierUserKnown); until then
            // matchDetails.relevancyMatch.passed is sent as a non-blocking `true` placeholder by the
            // backend (company recognized, no user to evaluate yet), not an actual relevancy match, so
            // it must not be displayed as green here.
            key: 'relevancy',
            label: localize('smartQuoteCriterionRelevancy'),
            extracted: !carrierUserKnown
              ? '—'
              : (relevancyMismatch ? localize('smartQuoteValueNotMatched') : localize('smartQuoteValueMatched')),
            ok: !relevancyMismatch,
            hint: !carrierUserKnown
              ? localize('smartQuoteRelevancyNoUserHint')
              : ((relevancyMismatch && this.criterionHint(md.relevancyMatch)) || ''),
            // The dash state isn't an error (ok stays true), so it needs the tooltip shown even though
            // the table's default tooltip only surfaces on a failing (!ok) row — see the template's
            // `row.hintAlways` check.
            hintAlways: !carrierUserKnown,
          },
          {
            key: 'currency',
            label: localize('smartQuoteFieldCurrency'),
            extracted: val(q.currency),
            // Red when the currency is invalid (not one of the allowlisted SEK/EUR/USD).
            ok: ok('currency'),
            hint: warnHint('currency'),
          },
          {
            key: 'price',
            label: localize('smartQuoteFieldPrice'),
            extracted: q.price != null ? q.price : '—',
            // Red when the price is missing/invalid OR exceeds the request's price_limit
            // (priceOverLimit) — the latter now blocks creation too (581 §2.7).
            ok: ok('price') && !priceOverLimit,
            hint: (priceOverLimit && this.criterionHint(md.priceWithinLimit))
              ? this.criterionHint(md.priceWithinLimit)
              : warnHint('price'),
          },
          {
            key: 'transit',
            label: localize('smartQuoteFieldTransit'),
            extracted: q.estimatedTransitTimeDays != null ? q.estimatedTransitTimeDays : '—',
            ok: ok('estimatedTransitTimeDays'),
            hint: warnHint('transit'),
          },
          {
            key: 'validTo',
            label: localize('smartQuoteFieldValidTo'),
            extracted: validTo || '—',
            ok: ok('quoteValidTo.date'),
            hint: warnHint('valid'),
          },
        ];
      },
      // Extra parameters that enrich the quote but are not required to create it.
      optionalParamRows(item) {
        let q = (item.docupipe && item.docupipe.normalizedQuote) || {};
        let dt = (v) => v && (v.date || v.time) ? [v.date, v.time].filter(Boolean).join(' ') : '—';
        return [
          {key: 'reference', label: localize('smartQuoteFieldReference'), value: q.quoteReference || '—'},
          {key: 'booking', label: localize('smartQuoteFieldBooking'), value: dt(q.deadlineForBooking)},
          {key: 'collection', label: localize('smartQuoteFieldCollection'), value: dt(q.latestTimeForCollection)},
          {key: 'co2', label: localize('smartQuoteFieldCo2'), value: q.environmentalImpactKgCo2 != null ? q.environmentalImpactKgCo2 : '—'},
        ];
      },
      quoteComment(item) {
        return (item.docupipe && item.docupipe.normalizedQuote && item.docupipe.normalizedQuote.comment) || '';
      },
      statusMeta(item) {
        let meta = STATUS_META[item.status];
        if (meta) return {label: localize(meta.labelKey), color: meta.color};
        if (item.status && item.status.startsWith('failed_')) {
          return {label: localize('smartQuoteStatusFailed'), color: 'error'};
        }
        return {label: item.status || '—', color: 'grey'};
      },
      statusTooltip(item) {
        if (item.status && item.status.startsWith('failed_')) return this.reasonText(item) || null;
        if (item.status === 'blocked_for_creation') return this.reasonText(item) || null;
        if (item.status === 'skipped') return this.reasonText(item) || null;
        if (item.status === 'proposal_created') {
          return item.createdVia === 'auto' ? localize('smartQuoteCreatedViaAuto') : localize('smartQuoteCreatedViaManual');
        }
        return null;
      },
      lastStepText(item) {
        if (item.status === 'proposal_created') return localize('smartQuoteLastStepQuoteCreated');
        if (item.status === 'duplicate_proposal') {
          return this.interpolate('smartQuoteLastStepDuplicateProposal', {proposalId: item.proposalId});
        }
        if (item.status === 'processed_needs_review' || item.status === 'blocked_for_creation') {
          let missingFields = item.docupipe && item.docupipe.validation && item.docupipe.validation.missingFields;
          if (missingFields && missingFields.length) {
            return this.interpolate('smartQuoteLastStepMissingField', {field: missingFields[0]});
          }
          return localize('smartQuoteStatusProcessedNeedsReview');
        }
        if (item.status && item.status.startsWith('failed_')) {
          let reason = this.reasonText(item);
          return reason ? this.truncate(reason, 40) : localize('smartQuoteStatusFailed');
        }
        if (item.status === 'skipped') {
          let reason = this.reasonText(item);
          return reason ? this.truncate(reason, 40) : localize('smartQuoteStatusSkipped');
        }
        if (item.status === 'fetched') return localize('smartQuoteLastStepWaiting');
        if (item.status === 'processed_valid') return localize('smartQuoteLastStepReady');
        return '—';
      },
      senderAlreadyRemembered(item) {
        return !!(item.senderMapping && item.senderMapping.enabled);
      },
      updateItem(id, patch) {
        let index = this.items.findIndex(listItem => listItem.id === id);
        if (index !== -1) {
          this.$set(this.items, index, {...this.items[index], ...patch});
        }
        if (this.selectedItem && this.selectedItem.id === id) {
          this.selectedItem = {...this.selectedItem, ...patch};
        }
        // Keep the open Create proposal form in sync (e.g. after matching the carrier from inside it),
        // so its carrier status / submit gate reflect the fresh mapping without reopening the dialog.
        if (this.createProposalItem && this.createProposalItem.id === id) {
          this.createProposalItem = {...this.createProposalItem, ...patch};
        }
      },
      primaryAction(item) {
        if (item.status === 'proposal_created' || item.status === 'duplicate_proposal') {
          return {labelKey: 'smartQuoteActionViewQuote', handler: () => this.viewQuote(item)};
        }
        if (item.status === 'processed_needs_review' || item.status === 'blocked_for_creation') {
          return {labelKey: 'smartQuoteActionReview', handler: () => this.openDetails(item)};
        }
        if (item.status && item.status.startsWith('failed_')) {
          return {labelKey: 'smartQuoteActionRetry', handler: () => this.processAttachment(item), loadingKey: 'processingAttachmentIds'};
        }
        if (item.status === 'fetched') {
          return {labelKey: 'smartQuoteActionProcess', handler: () => this.processAttachment(item), loadingKey: 'processingAttachmentIds'};
        }
        if (item.status === 'processed_valid') {
          // The list no longer carries match/docupipe details, so eligibility can't be
          // pre-checked client-side here; the backend validates on click and any rejection
          // surfaces through the standard error snackbar.
          return {labelKey: 'smartQuoteCreateProposal', handler: () => this.openCreateProposalDialog(item)};
        }
        return {labelKey: 'smartQuoteActionViewDetails', handler: () => this.openDetails(item)};
      },
      // A row's primary button is disabled while its own action is in flight, and — for the
      // mutating Process/Retry action (the only one carrying a loadingKey) — also while the mailbox
      // is being processed. "Process mailbox" runs the shared Gmail sync and then rebuilds the whole
      // list, so a row-level Process click during that window would race the sync and act on a status
      // that is about to change under it (feedback: disable Process while the mailbox is processing).
      // View quote / Review / View details are navigations, so they stay usable.
      primaryActionDisabled(item) {
        let action = this.primaryAction(item);
        if (this.isLoading(item, action.loadingKey)) return true;
        return this.processingMailbox && !!action.loadingKey;
      },
      panelPrimaryAction(item) {
        // In the review drawer the primary button must be the actual next step. Returns null (button
        // hidden) when there is nothing useful to do — never a "Review"/"View details" that would
        // just reopen this drawer, and never a "Remember sender" for a sender already remembered
        // (that would be a no-op).
        if (item.status === 'processed_needs_review' || item.status === 'blocked_for_creation') {
          // Editing is decoupled from carrier matching: the Create proposal form opens for any record
          // under review so the admin can fill/correct the quote fields regardless of carrier state.
          // The carrier is matched from inside that form (a specific request-assigned user), and
          // creation stays gated on a matched carrier there.
          return {labelKey: 'smartQuoteCreateProposal', handler: () => this.openCreateProposalDialog(item)};
        }
        let action = this.primaryAction(item);
        return (action.labelKey === 'smartQuoteActionViewDetails' || action.labelKey === 'smartQuoteActionReview')
          ? null
          : action;
      },
      viewQuote(item) {
        if (item.requestExists && item.numericRequestId) {
          // '#quotes' lands straight on the request's Quotes tab (AdminRequestDetails reads the hash
          // into its `tab`) — this action is about the created quote, not the request's requirements.
          this.$router.push({name: 'admin-request-details', query: {request_id: item.numericRequestId}, hash: '#quotes'});
          return;
        }
        this.openDetails(item);
      },
      openEmail(item) {
        if (!item.gmailMessageId) return;
        window.open(`https://mail.google.com/mail/u/0/#all/${item.gmailMessageId}`, '_blank');
      },
      openDetails(item) {
        this.selectedItem = item;
        this.detailsDrawer = true;
        this.commentExpanded = false;
        this.loadAuditTrail(item.id);
        this.loadAttachmentDetail(item.id);
      },
      async loadAttachmentDetail(id) {
        this.detailLoading = true;
        try {
          let response = await api.getSmartQuoteAttachment(id);
          // The backend now returns the same carrier/status/matchDetails fields for the list and the
          // detail (no more on-the-fly re-derivation, so no divergence to reconcile) — patch through
          // the shared updateItem() path, same as every other action response, so the list row for
          // this record stays in sync with the drawer instead of only the drawer being refreshed.
          this.updateItem(id, response.data);
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.detailLoading = false;
        }
      },
      async loadAuditTrail(id) {
        this.auditEntries = [];
        this.auditLoading = true;
        try {
          let response = await api.fetchSmartQuoteAuditTrail(id);
          this.auditEntries = response.data.items;
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.auditLoading = false;
        }
      },
      actionLabel(action) {
        return ACTION_LABEL_KEYS[action] ? localize(ACTION_LABEL_KEYS[action]) : action;
      },
      auditMeta(action) {
        return AUDIT_META[action] || {icon: 'mdi-circle-small', color: 'grey'};
      },
      entryDetail(entry) {
        let d = entry.details || {};
        switch (entry.action) {
          case 'fetched': {
            let parts = [];
            if (d.senderEmail) parts.push(`${localize('smartQuoteAuditFrom')}: ${d.senderEmail}`);
            if (d.threadId) parts.push(`${localize('smartQuoteAuditThreadId')}: ${d.threadId}`);
            if (d.filename) parts.push(`${localize('smartQuoteAuditFile')}: ${d.filename}`);
            return parts.join(' • ');
          }
          case 'docupipe_extracted': {
            let parts = [];
            if (d.filename) parts.push(`${localize('smartQuoteAuditFile')}: ${d.filename}`);
            if (d.sizeBytes != null) parts.push(`${localize('smartQuoteAuditSize')}: ${Math.round(d.sizeBytes / 1024)} KB`);
            if (d.reused_from_attachment_id != null) parts.push(`${localize('smartQuoteAuditReusedFrom')} #${d.reused_from_attachment_id}`);
            return parts.join(' • ');
          }
          case 'processed': {
            let parts = [];
            if (d.price != null) parts.push(`${localize('price')}: ${d.price} ${d.currency || ''}`.trim());
            if (d.transitDays != null) parts.push(`${localize('smartQuoteAuditTransitTime')}: ${d.transitDays} ${localize('days')}`);
            return parts.join(' • ');
          }
          case 'processing_failed':
            return d.error || '';
          case 'proposal_created': {
            let parts = [];
            if (d.proposalId) parts.push(`#${d.proposalId}`);
            if (d.via) parts.push(localize(d.via === 'auto' ? 'smartQuoteCreatedViaAuto' : 'smartQuoteCreatedViaManual'));
            return parts.join(' • ');
          }
          case 'creation_skipped_duplicate': {
            let parts = [];
            if (d.existingProposalId) parts.push(`${localize('smartQuoteAuditExistingProposal')} #${d.existingProposalId}`);
            if (d.transportMode || d.price != null) {
              let priceStr = d.price != null ? `${d.price} ${d.currency || ''}`.trim() : '';
              parts.push([d.transportMode, priceStr].filter(Boolean).join(', '));
            }
            return parts.join(' • ');
          }
          case 'creation_blocked':
            return d.reason || '';
          case 'creation_failed':
            return d.error || '';
          case 'sender_remembered':
          case 'sender_updated':
          case 'sender_enabled':
          case 'sender_disabled':
          case 'sender_deleted':
            return d.senderEmail || '';
          case 'sync_failed':
            return d.error || d.reason || '';
          case 'notification_sent':
            return Array.isArray(d.recipients) ? d.recipients.join(', ') : '';
          case 'push_received':
            return d.notifiedHistoryId != null ? `${localize('smartQuoteAuditHistoryId')} #${d.notifiedHistoryId}` : '';
          default:
            return '';
        }
      },
      async processMailbox() {
        try {
          this.processingMailbox = true;
          let response = await api.processSmartQuoteMailbox();
          await this.loadAttachments();
          this.getStats();
          let newCount = response.data.newCount || 0;
          if (response.data.hadFailures) {
            let base = newCount > 0 ? `${localize('smartQuoteNewEmails')}: ${newCount}. ` : '';
            methods.showGlobalSnackbar('warning', `${base}${localize('smartQuoteSyncHadFailures')}`);
          } else if (newCount > 0) {
            methods.showGlobalSnackbar('success', `${localize('smartQuoteNewEmails')}: ${newCount}`);
          } else {
            methods.showGlobalSnackbar('primary', localize('smartQuoteNoNewEmails'));
          }
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.processingMailbox = false;
        }
      },
      async processAttachment(item) {
        if (this.processingAttachmentIds[item.id]) return;
        this.$set(this.processingAttachmentIds, item.id, true);
        try {
          let response = await api.processSmartQuoteAttachment(item.id);
          this.updateItem(item.id, response.data);
          this.getStats();
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.$delete(this.processingAttachmentIds, item.id);
        }
      },
      // Feedback round 581 §2.6: transport mode is no longer inherited from the request, so the
      // admin confirms/picks it in a dialog before every manual proposal creation — pre-filled
      // with the PDF-extracted value when DocuPipe recognized one, empty and required otherwise.
      openCreateProposalDialog(item) {
        let q = (item.docupipe && item.docupipe.normalizedQuote) || {};
        let vt = q.quoteValidTo || {};
        this.createProposalItem = item;
        this.createProposalErrors = {};
        // Pre-fill the draft from the PDF extraction; whatever DocuPipe missed is left empty for the
        // admin to fill in. Nothing is inherited from the request/carrier assignment (only from PDF).
        this.createProposalForm = {
          transportMode: q.transportMode || null,
          currency: q.currency || null,
          price: q.price != null ? q.price : null,
          transitDays: q.estimatedTransitTimeDays != null ? q.estimatedTransitTimeDays : null,
          validToDate: item.validToDate || vt.date || null,
          validToTime: item.validToTime || vt.time || null,
        };
        // Default the Company user picker to the auto-matched carrier user, then load the matched
        // company's roster so the admin can switch to a relevant colleague if this one isn't.
        let mapping = (item.docupipe && item.docupipe.carrierMapping) || {};
        this.createProposalUsers = [];
        this.createProposalUserId = mapping.carrierUserId || null;
        this.createProposalDialog = true;
        this.loadCreateProposalUsers(item);
      },
      async loadCreateProposalUsers(item) {
        let mapping = (item && item.docupipe && item.docupipe.carrierMapping) || {};
        // A roster is only offered when a concrete carrier user was matched (the picker's v-if) — a
        // company-level-only match (no carrierUserId) still needs "Remember sender" first, so skip the
        // load there rather than fetch a roster the backend would refuse an override against.
        if (!mapping.companyId || !mapping.carrierUserId || !this.carrierMatched(item)) return;
        try {
          this.createProposalUsersLoading = true;
          let response = await api.getSmartQuoteCompanyCarriers(item.id, mapping.companyId);
          let data = response.data || {};
          // Render the whole company roster; users whose relevancy doesn't cover this request are just
          // disabled (item-disabled in the template), never dropped — same convention as Remember sender.
          this.createProposalUsers = (data.users || []).map(user => ({
            ...user,
            disabled: user.relevantToRequest === false,
          }));
        } catch (e) {
          // Non-blocking: without the roster the dialog falls back to the auto-matched user only.
        } finally {
          this.createProposalUsersLoading = false;
        }
      },
      closeCreateProposalDialog() {
        this.createProposalDialog = false;
        this.createProposalItem = null;
        this.createProposalErrors = {};
        this.createProposalUsers = [];
        this.createProposalUserId = null;
      },
      clearCreateProposalError(field) {
        if (this.createProposalErrors[field]) this.$delete(this.createProposalErrors, field);
      },
      // Commit the picked valid-to date into the menu's return value (closes the menu) and clear any
      // stale backend error — same save() idiom the Monitor's date-range filter uses.
      saveCreateProposalValidTo(date) {
        this.$refs.createProposalValidToMenu.save(date);
        this.clearCreateProposalError('valid_to_date');
      },
      // A matchDetails criterion ({passed, comment, ...}) of the record being edited, or null.
      createProposalCriterion(key) {
        return this.matchDetails(this.createProposalItem)[key] || null;
      },
      buildCreateProposalPayload() {
        let f = this.createProposalForm;
        let payload = {};
        let put = (key, value) => {
          if (value !== null && value !== undefined && value !== '') payload[key] = value;
        };
        put('transport_mode', f.transportMode);
        put('currency', f.currency);
        put('price', f.price);
        put('estimated_transit_time_days', f.transitDays);
        put('valid_to_date', f.validToDate);
        put('valid_to_time', f.validToTime);
        // Carrier-user override: sent only when the admin picked a colleague other than the
        // auto-matched default. The backend validates membership + relevancy and creates under them.
        if (this.createProposalUserId && this.createProposalUserId !== this.createProposalMatchedUserId) {
          payload.carrier_user_id = this.createProposalUserId;
        }
        return payload;
      },
      confirmCreateProposalDialog() {
        if (!this.createProposalForm.transportMode || !this.createProposalItem) return;
        this.createProposal(this.createProposalItem, this.buildCreateProposalPayload());
      },
      async createProposal(item, payload) {
        if (this.creatingProposalIds[item.id]) return;
        this.$set(this.creatingProposalIds, item.id, true);
        try {
          let response = await api.createSmartQuoteProposal(item.id, payload);
          this.updateItem(item.id, response.data);
          this.getStats();
          this.closeCreateProposalDialog();
        } catch (e) {
          let validation = e.response && e.response.status === 422 && e.response.data && e.response.data.errors;
          if (validation && validation.id) {
            // Record-level rejection (a proposal was already created from this attachment in the
            // background between the list load and this click, or it became dismissed/skipped): the
            // status the dialog was opened on is now stale. Pull the fresh row so its own action
            // self-corrects (e.g. "Create quote" -> "View quote"), and close the dialog when the
            // record can no longer be turned into a quote — re-submitting the same form would only
            // fail again. A still-creatable rejection (e.g. carrier not yet matched) keeps the dialog
            // open with the message so the admin can fix it in place.
            methods.showGlobalSnackbar('error', validation.id[0]);
            await this.refreshAttachment(item.id);
            this.getStats();
            if (!this.canCreateProposalFor(this.createProposalItem)) {
              this.closeCreateProposalDialog();
            }
          } else if (validation) {
            // Per-field 422 (transport_mode/currency/price/...): keep the dialog open and map the
            // errors onto the inputs so the admin can correct them without losing their place.
            this.createProposalErrors = validation;
          } else {
            methods.showGlobalSnackbar('error', methods.getErrors(e.response));
          }
        } finally {
          this.$delete(this.creatingProposalIds, item.id);
        }
      },
      // Whether a record can still be turned into a quote: only while it is processed but carries no
      // proposal yet. Once it has a proposalId or reached a terminal status the Create-quote form is
      // a no-op, so it must not stay open.
      canCreateProposalFor(item) {
        if (!item || item.proposalId) return false;
        return ['processed_valid', 'processed_needs_review', 'blocked_for_creation'].includes(item.status);
      },
      // Read-only pull of one row's current state — never re-processes or auto-creates (see the
      // backend SmartQuoteService::attachmentDetail()). Used to reconcile a row whose cached status
      // has gone stale under an action.
      async refreshAttachment(id) {
        try {
          let response = await api.getSmartQuoteAttachment(id);
          this.updateItem(id, response.data);
        } catch (e) {
          // Non-fatal: the row keeps its current status until the next full list refresh.
        }
      },
      // "Dismiss" closes a quote nobody will act on. Terminal and irreversible on the backend, and
      // only allowed while no proposal is linked to the attachment — an attachment that produced a
      // quote documents real history and must stay visible.
      canDismiss(item) {
        // `skipped` (feature 581) is already a terminal, read-only "nothing to do" state — like
        // `dismissed`, there is nothing left to dismiss (the backend refuses it too).
        return !!item && !item.proposalId && item.status !== 'dismissed' && item.status !== 'skipped';
      },
      openDismissDialog(item) {
        this.dismissItem = item;
        this.dismissDialog = true;
      },
      closeDismissDialog() {
        this.dismissDialog = false;
        this.dismissItem = null;
      },
      async confirmDismiss() {
        let item = this.dismissItem;
        if (!item || this.dismissingIds[item.id]) return;
        this.$set(this.dismissingIds, item.id, true);
        try {
          let response = await api.dismissSmartQuoteAttachment(item.id);
          this.updateItem(item.id, response.data);
          this.getStats();
          this.closeDismissDialog();
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.$delete(this.dismissingIds, item.id);
        }
      },
      carrierKnown(item) {
        return !!item.senderMapping;
      },
      onRememberSenderClick(item) {
        if (this.carrierKnown(item)) {
          this.rememberSender(item);
        } else {
          this.openRememberSenderDialog(item);
        }
      },
      openRememberSenderDialog(item) {
        this.rememberSenderItem = item;
        this.rememberSenderError = null;
        this.rememberSenderReassignConflict = null;
        this.rememberSenderCompanySearch = '';
        // Pre-fill the popup with the carrier already mapped/matched for this attachment (from the
        // carrier mapping, or the persisted sender mapping) instead of opening blank — the admin
        // usually just needs to pin the specific request-assigned user to turn an "unmatched"
        // company mapping into a match.
        let mapping = (item.docupipe && item.docupipe.carrierMapping) || {};
        let companyId = mapping.companyId || (item.senderMapping && item.senderMapping.companyId) || null;
        this.rememberSenderCompanyId = companyId;
        this.rememberSenderUserId = null;
        this.rememberSenderPreselectUserId = mapping.carrierUserId || null;
        // Seed the company autocomplete so the pre-selected company shows by name before any search.
        // Prefer the persisted sender-mapping company name (581 §2.7: it's now returned even when no
        // user is assigned), then the matched carrier name — never the raw "#id" placeholder, that
        // read as a broken label rather than an unresolved name.
        this.rememberSenderCompanies = companyId
          ? [{id: companyId, title: (item.senderMapping && item.senderMapping.companyName) || item.carrierName || ''}]
          : [];
        this.rememberSenderUsers = [];
        this.rememberSenderDialog = true;
        this.searchRememberSenderCompanies();
        if (companyId) this.loadRememberSenderUsers(companyId);
      },
      closeRememberSenderDialog() {
        this.rememberSenderDialog = false;
        this.rememberSenderItem = null;
        this.rememberSenderError = null;
        this.rememberSenderReassignConflict = null;
      },
      async searchRememberSenderCompanies() {
        try {
          this.rememberSenderCompaniesLoading = true;
          // `page` is required by CompaniesRequest — omitting it 422s and leaves the picker empty.
          let response = await api.getAdminCompanies({search: this.rememberSenderCompanySearch, per_page: 20, page: 1});
          let list = response.data.data;
          // Keep the pre-selected company visible even when the default search page doesn't include
          // it, so the autocomplete keeps rendering its name.
          if (this.rememberSenderCompanyId && !list.some(c => c.id === this.rememberSenderCompanyId)) {
            let current = this.rememberSenderCompanies.find(c => c.id === this.rememberSenderCompanyId);
            if (current) list = [current, ...list];
          }
          this.rememberSenderCompanies = list;
        } catch (e) {
          // Non-blocking — the admin can retry by typing again.
        } finally {
          this.rememberSenderCompaniesLoading = false;
        }
      },
      onRememberSenderCompanyChange() {
        // User picked a different company — clear the stale error/conflict and pinned user, then load users.
        this.rememberSenderError = null;
        this.rememberSenderReassignConflict = null;
        this.rememberSenderUserId = null;
        this.rememberSenderPreselectUserId = null;
        this.loadRememberSenderUsers(this.rememberSenderCompanyId);
      },
      async loadRememberSenderUsers(companyId) {
        this.rememberSenderUsers = [];
        if (!companyId || !this.rememberSenderItem) return;
        try {
          this.rememberSenderUsersLoading = true;
          // 581 §2.7: company-carriers replaces the plain company-users lookup — it also reports, per
          // user, whether THIS request's relevancy actually covers them (relevantToRequest), which the
          // old endpoint had no notion of (it only had a static company-level has_relevancy).
          let response = await api.getSmartQuoteCompanyCarriers(this.rememberSenderItem.id, companyId);
          let data = response.data || {};
          // Keep the Company field's display name in sync with the endpoint's answer, in case the
          // initial prefill (openRememberSenderDialog) only had the mapped carrier/sender name and this
          // resolves it to the authoritative company name — but only while the admin hasn't since
          // picked a different company.
          if (data.companyName && this.rememberSenderCompanyId === companyId) {
            let others = this.rememberSenderCompanies.filter(c => c.id !== companyId);
            this.rememberSenderCompanies = [{id: companyId, title: data.companyName}, ...others];
          }
          // Render every user — including ones not relevant to this request — so the admin sees the
          // full company roster; irrelevant ones are just disabled (item-disabled in the template),
          // never dropped from the list.
          this.rememberSenderUsers = (data.users || []).map(user => ({
            ...user,
            disabled: user.relevantToRequest === false,
          }));
          // Reopening an existing mapping: pre-select the request-assigned user when it belongs here.
          if (this.rememberSenderPreselectUserId
            && this.rememberSenderUsers.some(u => u.id === this.rememberSenderPreselectUserId)) {
            this.rememberSenderUserId = this.rememberSenderPreselectUserId;
          }
        } catch (e) {
          // Non-blocking — the sender can still be remembered at company level.
        } finally {
          this.rememberSenderPreselectUserId = null;
          this.rememberSenderUsersLoading = false;
        }
      },
      confirmRememberSenderDialog() {
        if (!this.rememberSenderCompanyId || !this.rememberSenderItem) return;
        this.rememberSender(this.rememberSenderItem, this.rememberSenderCompanyId, this.rememberSenderUserId);
      },
      // The admin has seen the reassign warning and confirmed — resend the same payload with
      // confirm_reassign so the backend applies the mapping instead of 409-ing again.
      confirmReassignSenderDialog() {
        if (!this.rememberSenderCompanyId || !this.rememberSenderItem) return;
        this.rememberSender(this.rememberSenderItem, this.rememberSenderCompanyId, this.rememberSenderUserId, true);
      },
      async rememberSender(item, companyId = null, userId = null, confirmReassign = false) {
        if (this.rememberingSenderIds[item.id]) return;
        this.$set(this.rememberingSenderIds, item.id, true);
        try {
          let payload = {};
          if (companyId) payload.company_id = companyId;
          if (companyId && userId) payload.user_id = userId;
          if (confirmReassign) payload.confirm_reassign = true;
          let response = await api.rememberSmartQuoteSender(item.id, payload);
          this.rememberSenderReassignConflict = null;
          // The endpoint re-matches the attachment against the sender mapping it just wrote and
          // returns the refreshed full item under `attachment` — merge it wholesale so the row's
          // status/carrier/creatability update without a separate "Process" round trip.
          let attachment = response.data.attachment;
          this.updateItem(item.id, attachment);
          this.getStats();
          let md = this.matchDetails(attachment);
          let carrierMatched = this.carrierMatched(attachment);
          if (!carrierMatched && this.rememberSenderDialog) {
            // Mapping saved, but the picked carrier still isn't matched for this request (it's not
            // assigned to it). Don't claim success — keep the dialog open with the reason so the admin
            // can pick a carrier from the "assigned to this request" list instead.
            let reason = (md.carrierMapping && md.carrierMapping.comment) || '';
            this.rememberSenderError = (localize('smartQuoteSenderSavedNotMatched') + ' ' + reason).trim();
            return;
          }
          methods.showGlobalSnackbar('success', localize('smartQuoteSenderRemembered'));
          this.closeRememberSenderDialog();
          // Matching the carrier can auto-create the proposal when the record was otherwise complete
          // — the open Create proposal form is then moot, so close it.
          if (this.createProposalDialog && this.createProposalItem
            && this.createProposalItem.id === item.id && this.createProposalItem.proposalId) {
            this.closeCreateProposalDialog();
          }
        } catch (e) {
          let resp = e.response;
          // The sender is already mapped to a different company: the backend refuses to silently
          // reassign it and 409s with the conflict details instead of a plain error. Show that as a
          // confirmation step in the dialog rather than a dead-end error.
          if (this.rememberSenderDialog && resp && resp.status === 409
            && resp.data && resp.data.conflict === 'sender_reassign') {
            this.rememberSenderError = null;
            this.rememberSenderReassignConflict = resp.data;
            return;
          }
          // Surface the reason inside the picker (e.g. "already mapped to company #2 — disable that
          // mapping first") instead of only a snackbar that's easy to miss; fall back to the snackbar
          // when the remember was triggered without the dialog (row "…" menu).
          let msg = methods.getErrors(e.response);
          if (this.rememberSenderDialog) {
            this.rememberSenderError = msg;
          } else {
            methods.showGlobalSnackbar('error', msg);
          }
        } finally {
          this.$delete(this.rememberingSenderIds, item.id);
        }
      },
      async openPdf(item) {
        if (this.pdfLoadingIds[item.id]) return;
        this.$set(this.pdfLoadingIds, item.id, true);
        try {
          let response = await api.fetchSmartQuotePdf(item.id);
          let blob = new Blob([response.data], {type: 'application/pdf'});
          let url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.$delete(this.pdfLoadingIds, item.id);
        }
      },
      truncate(text, length) {
        if (!text) return '';
        return text.length > length ? `${text.slice(0, length)}…` : text;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .smart-quote-kv {
    background: transparent;

    // Compact rows that wrap long values (carrier names, filenames) instead of forcing the
    // 400px drawer to overflow horizontally.
    ::v-deep th,
    ::v-deep td {
      height: auto !important;
      padding: 2px 8px 2px 0 !important;
      font-size: 12px;
      word-break: break-word;
    }
  }

  .smart-quote-drawer-actions {
    gap: 8px;
  }

  .smart-quote-form-carrier {
    background: rgba(0, 0, 0, 0.03);
    border-radius: 4px;
  }

  .smart-quote-param-invalid td {
    background: rgba(244, 67, 54, 0.06);
  }

  .smart-quote-hintable {
    cursor: help;
    border-bottom: 1px dashed currentColor;
  }

  .smart-quote-comment {
    white-space: pre-wrap;
    word-break: break-word;
  }

  .smart-quote-actions {
    gap: 4px;
    flex-wrap: nowrap;
    white-space: nowrap;
  }

  .smart-quote-attachment-link {
    display: flex;
    align-items: center;
    max-width: 100%;
    text-decoration: none;

    ::v-deep .v-icon {
      flex: none;
    }
  }

  .smart-quote-cell-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    flex: 1 1 auto;
  }

  .smart-quote-stat-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    height: 100%;
    box-sizing: border-box;
    border-radius: 8px;
    border: 1px solid transparent;

    &--info {
      background: rgba(33, 150, 243, 0.08);
      border-color: rgba(33, 150, 243, 0.35);
    }

    &--success {
      background: rgba(135, 201, 37, 0.1);
      border-color: rgba(135, 201, 37, 0.4);
    }

    &--warning {
      background: rgba(233, 176, 28, 0.12);
      border-color: rgba(233, 176, 28, 0.45);
    }

    &--error {
      background: rgba(255, 82, 82, 0.08);
      border-color: rgba(255, 82, 82, 0.35);
    }
  }

  .smart-quote-stat-card__hint-icon {
    position: absolute;
    top: 6px;
    right: 6px;
    opacity: 0.55;
  }

  .smart-quote-stat-card__icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;

    &--info {
      background: rgba(33, 150, 243, 0.15);
    }

    &--success {
      background: rgba(135, 201, 37, 0.18);
    }

    &--warning {
      background: rgba(233, 176, 28, 0.2);
    }

    &--error {
      background: rgba(255, 82, 82, 0.15);
    }
  }

  .smart-quote-stat-card__text {
    min-width: 0;
    flex: 1 1 auto;
  }

  .smart-quote-stat-card__label {
    font-size: 13px;
    color: #666;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .smart-quote-stat-card__value {
    font-size: 26px;
    font-weight: 700;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .smart-quote-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;

    &--error {
      background: #ff5252;
    }

    &--warning {
      background: #e9b01c;
    }

    &--black {
      background: #000;
    }
  }

  .smart-quote-chip-active {
    background: rgba(0, 0, 0, 0.06) !important;
    font-weight: 600;
  }

  ::v-deep .smart-quote-row-selected {
    background-color: rgba(252, 129, 1, 0.06);
  }

  ::v-deep .smart-quote-table tbody tr {
    cursor: pointer;
  }

  ::v-deep .smart-quote-table table {
    table-layout: fixed;
  }

  ::v-deep .smart-quote-table td {
    overflow: hidden;
  }

  .smart-quote-per-page {
    max-width: 130px;
  }

  .smart-quote-detail-fields {
    background: transparent;
    table-layout: fixed;
    width: 100%;

    td {
      padding: 4px 0 !important;
      border: none !important;
      vertical-align: top;
      word-break: break-word;
    }

    td:first-child {
      width: 40%;
    }
  }

  .smart-quote-timeline-time {
    width: 44px;
    flex: none;
    padding-top: 2px;
  }

  .smart-quote-timeline-content {
    flex: 1 1 auto;
    min-width: 0;
    word-break: break-word;
  }

  ::v-deep .smart-quote-drawer .v-navigation-drawer__content {
    overflow-x: hidden;
  }
</style>
